(() => {
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const MOBILE_QUERY = '(max-width: 979px)';

  const slugify = (value) => {
    const slug = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

    return slug || 'article-section';
  };

  class AutelArticleLayout extends HTMLElement {
    connectedCallback() {
      this.headingObserver?.disconnect();
      this.mobileMedia?.removeEventListener?.('change', this.handleViewportChange);
      this.copyTimers?.forEach((timer) => window.clearTimeout(timer));
      this.newsletterRequests?.forEach((controller) => controller.abort());
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.copyTimers = new Map();
      this.newsletterRequests = new Map();
      this.setupTableOfContents();
      this.setupCopyActions();
      this.setupNewsletterForms();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.headingObserver?.disconnect();
      this.mobileMedia?.removeEventListener?.('change', this.handleViewportChange);
      this.copyTimers?.forEach((timer) => window.clearTimeout(timer));
      this.copyTimers?.clear();
      this.newsletterRequests?.forEach((controller) => controller.abort());
      this.newsletterRequests?.clear();
    }

    setupTableOfContents() {
      const article = this.querySelector('[data-article-content]');
      const list = this.querySelector('[data-toc-list]');
      const empty = this.querySelector('[data-toc-empty]');
      const details = this.querySelector('[data-toc-details]');

      if (!article || !list || !details) return;

      const headings = Array.from(article.querySelectorAll('h2, h3'));
      list.replaceChildren();

      if (!headings.length) {
        empty?.removeAttribute('hidden');
        details.setAttribute('data-empty', 'true');
        return;
      }

      empty?.setAttribute('hidden', '');
      details.removeAttribute('data-empty');

      const usedIds = new Set();
      let currentParentId = '';

      headings.forEach((heading, index) => {
        let headingId = heading.id || slugify(heading.textContent || '');
        const baseId = headingId;
        let suffix = 2;

        while (usedIds.has(headingId) || (document.getElementById(headingId) && document.getElementById(headingId) !== heading)) {
          headingId = `${baseId}-${suffix}`;
          suffix += 1;
        }

        heading.id = headingId;
        usedIds.add(headingId);

        if (heading.tagName === 'H2') currentParentId = headingId;

        const item = document.createElement('li');
        const link = document.createElement('a');
        const level = heading.tagName === 'H3' ? '3' : '2';

        item.className = 'aea-toc__item';
        item.dataset.level = level;
        item.dataset.headingId = headingId;
        item.dataset.parentId = level === '3' ? currentParentId : headingId;
        link.href = `#${headingId}`;
        link.textContent = heading.textContent.trim() || `Section ${index + 1}`;
        item.append(link);
        list.append(item);
      });

      list.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#"]');
        if (!link) return;

        const targetId = decodeURIComponent(link.hash.slice(1));
        const target = document.getElementById(targetId);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia(REDUCED_MOTION_QUERY).matches ? 'auto' : 'smooth',
          block: 'start'
        });
        window.history.replaceState(null, '', `#${encodeURIComponent(targetId)}`);

        if (this.mobileMedia?.matches) details.removeAttribute('open');
      }, { signal: this.abortController.signal });

      this.mobileMedia = window.matchMedia(MOBILE_QUERY);
      this.handleViewportChange = (event) => {
        if (event.matches) {
          details.removeAttribute('open');
        } else {
          details.setAttribute('open', '');
        }
      };
      this.handleViewportChange(this.mobileMedia);
      this.mobileMedia.addEventListener?.('change', this.handleViewportChange);

      const items = Array.from(list.querySelectorAll('[data-heading-id]'));
      const setActive = (headingId) => {
        const activeItem = items.find((item) => item.dataset.headingId === headingId) || items[0];
        if (!activeItem) return;

        const activeParentId = activeItem.dataset.parentId;
        items.forEach((item) => {
          const isActive = item === activeItem;
          const link = item.querySelector('a');
          item.classList.toggle('is-active', isActive);
          item.dataset.parentActive = String(item.dataset.level === '3' && item.dataset.parentId === activeParentId);
          if (isActive) link?.setAttribute('aria-current', 'location');
          else link?.removeAttribute('aria-current');
        });
      };

      setActive(headings[0].id);
      this.headingObserver?.disconnect();
      this.headingObserver = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => Math.abs(a.boundingClientRect.top) - Math.abs(b.boundingClientRect.top));
        if (visible[0]) setActive(visible[0].target.id);
      }, {
        rootMargin: '-18% 0px -68% 0px',
        threshold: [0, 1]
      });
      headings.forEach((heading) => this.headingObserver.observe(heading));
    }

    setupCopyActions() {
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-copy-url]');
        if (!button) return;

        const label = button.querySelector('[data-copy-label]');
        const status = button.closest('.aea-footer-share')?.querySelector('[data-copy-status]');
        const defaultLabel = button.dataset.defaultLabel || 'Copy link';
        const successLabel = button.dataset.successLabel || 'Copied';
        const url = button.dataset.copyUrl || window.location.href;

        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
          await navigator.clipboard.writeText(url);
          if (label) label.textContent = successLabel;
          if (status) status.textContent = successLabel;

          window.clearTimeout(this.copyTimers.get(button));
          const timer = window.setTimeout(() => {
            if (label) label.textContent = defaultLabel;
            if (status) status.textContent = '';
            this.copyTimers.delete(button);
          }, 1800);
          this.copyTimers.set(button, timer);
        } catch (_error) {
          if (status) status.textContent = 'Copy failed. Please copy the page address from your browser.';
        }
      }, { signal: this.abortController.signal });
    }

    setupNewsletterForms() {
      this.addEventListener('submit', async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.classList.contains('aea-newsletter__form')) return;
        if (event.defaultPrevented) return;

        if (!form.checkValidity()) {
          form.reportValidity();
          return;
        }

        event.preventDefault();
        if (form.dataset.aeaSubmitting === 'true') return;

        form.dataset.aeaSubmitting = 'true';
        form.setAttribute('aria-busy', 'true');

        const submitButton = form.querySelector('[data-aea-newsletter-submit]');
        const status = form.querySelector('[data-aea-newsletter-status]');
        const emailInput = form.querySelector('input[type="email"]');
        const defaultLabel = submitButton?.dataset.defaultLabel || submitButton?.textContent || 'Subscribe';
        const submittingLabel = submitButton?.dataset.submittingLabel || 'Subscribing...';
        const controller = new AbortController();
        this.newsletterRequests.get(form)?.abort();
        this.newsletterRequests.set(form, controller);

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = submittingLabel;
        }

        const showStatus = (message, state) => {
          if (!status) return;
          status.textContent = message;
          status.hidden = false;
          status.classList.toggle('aea-form-message--success', state === 'success');
          status.classList.toggle('aea-form-message--error', state === 'error');
          status.setAttribute('role', state === 'error' ? 'alert' : 'status');
          status.setAttribute('aria-live', state === 'error' ? 'assertive' : 'polite');
          if (state === 'error') emailInput?.setAttribute('aria-invalid', 'true');
          else emailInput?.removeAttribute('aria-invalid');
          status.focus({ preventScroll: true });
        };

        try {
          const response = await window.fetch(form.action, {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });
          const responseHtml = await response.text();
          if (!form.isConnected || controller.signal.aborted) return;

          const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
          const returnedForm = responseDocument.getElementById(form.id);
          const returnedSuccess = returnedForm?.querySelector('[data-aea-newsletter-success]');
          const returnedErrors = returnedForm?.querySelector('[data-aea-newsletter-errors]');

          if (response.ok && returnedSuccess) {
            form.classList.add('is-success');
            form.reset();
            showStatus(returnedSuccess.textContent.trim() || status?.dataset.successMessage || 'Thanks for subscribing.', 'success');
          } else if (returnedErrors?.textContent.trim()) {
            form.classList.remove('is-success');
            showStatus(returnedErrors.textContent.trim(), 'error');
          } else {
            form.classList.remove('is-success');
            showStatus(status?.dataset.errorMessage || 'We could not confirm your subscription. Please try again.', 'error');
          }
        } catch (error) {
          if (error.name !== 'AbortError' && form.isConnected) {
            form.classList.remove('is-success');
            showStatus(status?.dataset.errorMessage || 'We could not confirm your subscription. Please try again.', 'error');
          }
        } finally {
          const isCurrentRequest = this.newsletterRequests.get(form) === controller;
          if (isCurrentRequest) this.newsletterRequests.delete(form);
          if (isCurrentRequest && form.isConnected) {
            delete form.dataset.aeaSubmitting;
            form.removeAttribute('aria-busy');
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.textContent = defaultLabel;
            }
          }
        }
      }, { signal: this.abortController.signal });
    }
  }

  class AutelArticleCarousel extends HTMLElement {
    connectedCallback() {
      this.visibilityObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.reducedMotion?.removeEventListener?.('change', this.handleMotionChange);
      window.clearTimeout(this.autoplayTimer);
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = Math.max(3000, Number(this.dataset.interval) || 6000);
      this.currentIndex = 0;
      this.isVisible = true;
      this.isPaused = false;
      this.isPausedByUser = false;
      this.refresh();
      this.bindEvents();
      this.observeVisibility();
      this.setAttribute('data-carousel-ready', '');
      this.restartAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.reducedMotion?.removeEventListener?.('change', this.handleMotionChange);
      window.clearTimeout(this.autoplayTimer);
    }

    refresh() {
      this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-carousel-dot]'));
      this.status = this.querySelector('[data-carousel-status]');
      this.autoplayToggle = this.querySelector('[data-carousel-toggle]');
      if (!this.slides.length) return;
      this.currentIndex = Math.min(this.currentIndex, this.slides.length - 1);
      this.goTo(this.currentIndex, false);
      this.updateAutoplayToggle();
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal });
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal });

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.goTo(Number(dot.dataset.carouselDot)), { signal });
      });

      this.autoplayToggle?.addEventListener('click', () => {
        this.isPausedByUser = !this.isPausedByUser;
        this.updateAutoplayToggle();
        this.restartAutoplay();
      }, { signal });

      this.addEventListener('mouseenter', () => this.pause(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });
      this.addEventListener('focusin', () => this.pause(), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.resume();
      }, { signal });

      this.handleMotionChange = () => {
        this.updateAutoplayToggle();
        this.restartAutoplay();
      };
      this.reducedMotion.addEventListener?.('change', this.handleMotionChange);

      document.addEventListener('shopify:block:select', (event) => {
        const slide = event.target?.closest?.('[data-carousel-slide]');
        const index = this.slides?.indexOf(slide);
        if (index >= 0 && this.contains(slide)) {
          this.goTo(index, false);
          this.pause();
        }
      }, { signal });

      const viewport = this.querySelector('[data-carousel-viewport]');
      if (viewport) {
        this.mutationObserver = new MutationObserver(() => this.refresh());
        this.mutationObserver.observe(viewport, { childList: true });
      }
    }

    observeVisibility() {
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = Boolean(entry?.isIntersecting);
        this.restartAutoplay();
      }, { threshold: 0.1 });
      this.visibilityObserver.observe(this);
    }

    goTo(requestedIndex, restart = true) {
      if (!this.slides?.length) return;
      const length = this.slides.length;
      this.currentIndex = ((requestedIndex % length) + length) % length;

      this.slides.forEach((slide, index) => {
        const isCurrent = index === this.currentIndex;
        slide.classList.toggle('is-active', isCurrent);
        slide.setAttribute('aria-hidden', String(!isCurrent));
        if ('inert' in slide) slide.inert = !isCurrent;
      });

      this.dots?.forEach((dot, index) => {
        if (index === this.currentIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });

      const heading = this.slides[this.currentIndex].querySelector('h3, h4')?.textContent?.trim();
      if (this.status) {
        this.status.textContent = `Slide ${this.currentIndex + 1} of ${length}${heading ? `: ${heading}` : ''}`;
      }

      if (restart) this.restartAutoplay();
    }

    pause() {
      this.isPaused = true;
      window.clearTimeout(this.autoplayTimer);
    }

    resume() {
      this.isPaused = false;
      this.restartAutoplay();
    }

    updateAutoplayToggle() {
      if (!this.autoplayToggle) return;
      const reducedMotion = Boolean(this.reducedMotion?.matches);
      this.autoplayToggle.hidden = reducedMotion || !this.autoplayEnabled || this.slides?.length < 2;
      this.autoplayToggle.setAttribute('aria-pressed', String(this.isPausedByUser));
      const label = this.autoplayToggle.querySelector('[data-carousel-toggle-label]');
      if (label) {
        label.textContent = this.isPausedByUser
          ? (this.autoplayToggle.dataset.playLabel || 'Play slideshow')
          : (this.autoplayToggle.dataset.pauseLabel || 'Pause slideshow');
      }
    }

    restartAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      if (!this.autoplayEnabled || this.isPaused || this.isPausedByUser || !this.isVisible || this.reducedMotion.matches || this.slides?.length < 2) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1, false);
        this.restartAutoplay();
      }, this.interval);
    }
  }

  class AutelArticleScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.reducedMotion?.removeEventListener?.('change', this.handleMotionChange);
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.scrollSettleTimer);
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.carousel = this.querySelector('[data-scroll-carousel]');
      this.items = this.carousel
        ? Array.from(this.carousel.querySelectorAll('[data-carousel-slide]:not([data-carousel-clone])'))
        : [];
      if (!this.carousel || !this.items.length) return;

      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.interval = Math.max(3000, Number(this.dataset.interval) || 6000);
      this.currentIndex = 0;
      this.isVisible = true;
      this.isPaused = false;
      this.lastInputModality = 'keyboard';
      this.hasKeyboardFocusWithin = false;
      this.status = this.querySelector('[data-carousel-status]');
      this.setupLoop();
      this.bindEvents();
      this.observeVisibility();
      this.observeSize();
      this.observeSlides();
      this.updateStatus();
      this.restartAutoplay();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.reducedMotion?.removeEventListener?.('change', this.handleMotionChange);
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.scrollSettleTimer);
    }

    setupLoop() {
      this.carousel.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      if (this.items.length < 2) {
        this.physicalSlides = [...this.items];
        return;
      }

      const leadingClone = this.createClone(this.items[this.items.length - 1], 'last');
      const trailingClone = this.createClone(this.items[0], 'first');
      this.carousel.prepend(leadingClone);
      this.carousel.append(trailingClone);
      this.physicalSlides = Array.from(this.carousel.querySelectorAll('[data-carousel-slide]'));
      this.jumpToPhysical(1);
    }

    createClone(source, position) {
      const clone = source.cloneNode(true);
      clone.dataset.carouselClone = position;
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('inert', '');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => element.removeAttribute('data-shopify-editor-block'));
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    bindEvents() {
      const signal = this.abortController.signal;
      this.querySelector('[data-carousel-previous]')?.addEventListener('click', () => this.goTo(this.currentIndex - 1), { signal });
      this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.goTo(this.currentIndex + 1), { signal });

      this.carousel.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollSettleTimer);
        this.scrollSettleTimer = window.setTimeout(() => this.handleScrollSettled(), 140);
      }, { signal });
      this.carousel.addEventListener('scrollend', () => this.handleScrollSettled(), { signal });

      document.addEventListener('pointerdown', (event) => {
        this.lastInputModality = 'pointer';
        if (this.contains(event.target)) {
          this.dataset.inputModality = 'pointer';
          this.hasKeyboardFocusWithin = false;
        }
      }, { signal });
      document.addEventListener('keydown', () => {
        this.lastInputModality = 'keyboard';
        if (this.contains(document.activeElement)) {
          this.dataset.inputModality = 'keyboard';
          this.hasKeyboardFocusWithin = true;
          this.pause();
        }
      }, { signal });

      this.addEventListener('mouseenter', () => this.pause(), { signal });
      this.addEventListener('mouseleave', () => this.resume(), { signal });
      this.addEventListener('focusin', () => {
        this.dataset.inputModality = this.lastInputModality;
        this.hasKeyboardFocusWithin = this.lastInputModality === 'keyboard';
        this.pause();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.hasKeyboardFocusWithin = false;
          this.resume();
        }
      }, { signal });
      this.carousel.addEventListener('touchstart', () => this.pause(), { signal, passive: true });
      this.carousel.addEventListener('touchend', () => this.resume(), { signal, passive: true });
      this.carousel.addEventListener('touchcancel', () => this.resume(), { signal, passive: true });

      this.handleMotionChange = () => this.restartAutoplay();
      this.reducedMotion.addEventListener?.('change', this.handleMotionChange);
      document.addEventListener('visibilitychange', () => this.restartAutoplay(), { signal });

      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const selectedSlide = event.target.closest?.('[data-carousel-slide]:not([data-carousel-clone])');
        const selectedIndex = this.items.indexOf(selectedSlide);
        if (selectedIndex >= 0) this.goTo(selectedIndex, false);
        this.pause();
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (this.contains(event.target)) this.resume();
      }, { signal });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = Boolean(entry?.isIntersecting);
        this.restartAutoplay();
      }, { threshold: 0.1 });
      this.visibilityObserver.observe(this);
    }

    observeSize() {
      if (!('ResizeObserver' in window)) return;
      this.lastCarouselWidth = Math.round(this.carousel.getBoundingClientRect().width);
      this.resizeObserver = new ResizeObserver(([entry]) => {
        const nextWidth = Math.round(entry?.contentRect.width || 0);
        if (!nextWidth || nextWidth === this.lastCarouselWidth) return;
        this.lastCarouselWidth = nextWidth;
        this.jumpToPhysical(this.items.length > 1 ? this.currentIndex + 1 : 0);
      });
      this.resizeObserver.observe(this.carousel);
    }

    observeSlides() {
      if (!('MutationObserver' in window)) return;
      this.mutationObserver = new MutationObserver((mutations) => {
        const realSlidesChanged = mutations.some((mutation) => (
          [...mutation.addedNodes, ...mutation.removedNodes].some((node) => (
            node.nodeType === Node.ELEMENT_NODE && !node.hasAttribute('data-carousel-clone')
          ))
        ));
        if (realSlidesChanged) this.refreshSlides();
      });
      this.mutationObserver.observe(this.carousel, { childList: true });
    }

    refreshSlides() {
      const selectedItem = this.items[this.currentIndex];
      this.mutationObserver?.disconnect();
      window.clearTimeout(this.autoplayTimer);
      window.clearTimeout(this.scrollSettleTimer);
      this.carousel.querySelectorAll('[data-carousel-clone]').forEach((clone) => clone.remove());
      this.items = Array.from(this.carousel.querySelectorAll('[data-carousel-slide]'));

      if (!this.items.length) {
        this.physicalSlides = [];
        window.clearTimeout(this.autoplayTimer);
        if (this.status) this.status.textContent = '';
      } else {
        const selectedIndex = this.items.indexOf(selectedItem);
        this.currentIndex = selectedIndex >= 0
          ? selectedIndex
          : Math.min(this.currentIndex, this.items.length - 1);
        this.setupLoop();
        this.updateStatus();
        this.restartAutoplay();
      }

      this.mutationObserver?.observe(this.carousel, { childList: true });
    }

    goTo(requestedIndex, animate = !this.reducedMotion.matches) {
      if (!this.items.length) return;
      const length = this.items.length;
      let physicalIndex;

      if (length === 1) {
        this.currentIndex = 0;
        physicalIndex = 0;
      } else if (requestedIndex >= length) {
        this.currentIndex = 0;
        physicalIndex = length + 1;
      } else if (requestedIndex < 0) {
        this.currentIndex = length - 1;
        physicalIndex = 0;
      } else {
        this.currentIndex = requestedIndex;
        physicalIndex = requestedIndex + 1;
      }

      window.clearTimeout(this.autoplayTimer);
      this.scrollToPhysical(physicalIndex, animate);
      this.updateStatus();
      window.clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = window.setTimeout(() => this.handleScrollSettled(), animate ? 1200 : 0);
    }

    scrollToPhysical(index, animate) {
      const target = this.physicalSlides[index];
      if (!target) return;
      this.carousel.scrollTo({ left: target.offsetLeft, behavior: animate ? 'smooth' : 'auto' });
    }

    jumpToPhysical(index) {
      this.scrollToPhysical(index, false);
    }

    handleScrollSettled() {
      window.clearTimeout(this.scrollSettleTimer);
      if (!this.physicalSlides?.length) return;

      const physicalIndex = this.physicalSlides.reduce((closestIndex, slide, index) => {
        const currentDistance = Math.abs(slide.offsetLeft - this.carousel.scrollLeft);
        const closestDistance = Math.abs(this.physicalSlides[closestIndex].offsetLeft - this.carousel.scrollLeft);
        return currentDistance < closestDistance ? index : closestIndex;
      }, 0);

      if (this.items.length > 1 && physicalIndex === 0) {
        this.currentIndex = this.items.length - 1;
        this.jumpToPhysical(this.items.length);
      } else if (this.items.length > 1 && physicalIndex === this.physicalSlides.length - 1) {
        this.currentIndex = 0;
        this.jumpToPhysical(1);
      } else {
        this.currentIndex = this.items.length > 1 ? physicalIndex - 1 : 0;
      }

      this.updateStatus();
      this.restartAutoplay();
    }

    updateStatus() {
      if (!this.status || !this.items.length) return;
      const heading = this.items[this.currentIndex]?.querySelector('.ps-card-title')?.textContent?.trim();
      this.status.textContent = `Slide ${this.currentIndex + 1} of ${this.items.length}${heading ? `: ${heading}` : ''}`;
    }

    pause() {
      this.isPaused = true;
      window.clearTimeout(this.autoplayTimer);
    }

    resume() {
      this.isPaused = false;
      this.restartAutoplay();
    }

    restartAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      if (
        !this.autoplayEnabled
        || this.isPaused
        || this.matches(':hover')
        || this.hasKeyboardFocusWithin
        || !this.isVisible
        || document.hidden
        || this.reducedMotion.matches
        || this.items.length < 2
      ) return;

      this.autoplayTimer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1);
      }, this.interval);
    }
  }

  if (!customElements.get('autel-article-layout')) {
    customElements.define('autel-article-layout', AutelArticleLayout);
  }

  if (!customElements.get('autel-article-carousel')) {
    customElements.define('autel-article-carousel', AutelArticleCarousel);
  }

  if (!customElements.get('autel-article-scroll-carousel')) {
    customElements.define('autel-article-scroll-carousel', AutelArticleScrollCarousel);
  }
})();
